// Formularios de login y registro de la pantalla de auth. Extraídos de AuthUI.ts (god-object):
// sólo dependen de la AuthApi + callbacks, así que son funciones puras (sin estado del host).
import { AuthApi, type LoginResult, type RegisterResult } from '../../api/AuthApi';
import { createTextInput, createCheckbox, createButton } from '../formControls';

const LOGIN_REMEMBER_KEY = 'aureum_login_remember';

interface RememberedLogin {
  username: string;
  password: string;
}

export function renderLoginForm(
  api: AuthApi,
  container: HTMLDivElement,
  onDone: (sessionToken: string) => void,
  onSwitchToRegister: () => void
): void {
  container.innerHTML = '';
  container.setAttribute('data-lpignore', 'true');

  const info = document.createElement('p');
  info.textContent = 'Ingresa con tu cuenta y contraseña';
  info.style.textAlign = 'center';
  info.style.margin = '0 0 10px';

  const usernameField = createTextInput({
    id: 'login-username',
    name: 'username',
    placeholder: 'username',
    autocomplete: 'off',
  });
  const usernameInput = usernameField.input;
  usernameField.wrapper.style.marginBottom = '8px';

  const passwordField = createTextInput({
    id: 'login-password',
    name: 'password',
    placeholder: 'password',
    type: 'password',
    autocomplete: 'off',
  });
  const passwordInput = passwordField.input;
  passwordField.wrapper.style.marginBottom = '8px';

  // Checkbox "recordar datos de login"
  const rememberField = createCheckbox({ label: 'Recordar usuario y contraseña' });
  const rememberWrapper = rememberField.wrapper;
  rememberWrapper.style.fontSize = '12px';
  rememberWrapper.style.marginBottom = '4px';
  const rememberCheckbox = rememberField.input;

  const isDebug = import.meta.env.DEV;
  if (isDebug) {
    usernameInput.value = 'asdasd';
    passwordInput.value = 'asdasd';
  } else {
    // Rellenar si había datos recordados
    try {
      const stored = window.localStorage.getItem(LOGIN_REMEMBER_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as RememberedLogin;
        if (parsed?.username) {
          usernameInput.value = parsed.username;
        }
        if (parsed?.password) {
          passwordInput.value = parsed.password;
        }
        rememberCheckbox.checked = true;
      }
    } catch {
      // Ignorar errores de parseo
    }
  }

  const errorLabel = document.createElement('div');
  errorLabel.style.color = '#f55';
  errorLabel.style.minHeight = '20px';
  errorLabel.style.fontSize = '12px';

  const submit = createButton('Entrar', { fullWidth: true });
  submit.style.marginTop = '8px';

  submit.onclick = async () => {
    errorLabel.textContent = '';
    const username = usernameInput.value.trim();
    const password = passwordInput.value;
    if (!username || !password) {
      errorLabel.textContent = 'Completa usuario y contraseña.';
      return;
    }

    submit.disabled = true;
    const result: LoginResult = await api.login(username, password);
    submit.disabled = false;

    if (!result.success || !result.sessionToken) {
      errorLabel.textContent = result.error ?? 'Error de login';
      return;
    }

     // Guardar o limpiar datos recordados según el checkbox
    try {
      if (rememberCheckbox.checked) {
        const toStore: RememberedLogin = { username, password };
        window.localStorage.setItem(LOGIN_REMEMBER_KEY, JSON.stringify(toStore));
      } else {
        window.localStorage.removeItem(LOGIN_REMEMBER_KEY);
      }
    } catch {
      // Si falla el localStorage, no bloqueamos el login
    }

    onDone(result.sessionToken);
  };

  // Botón oficial "Entrar con Discord" (brand: blurple + logo de Discord). Usa la
  // variante .aureum-btn--discord → hereda la MISMA profundidad que "Entrar" (sombra,
  // bisel, brillo al hover, hundido al :active). OAuth via popup; en éxito usa el MISMO
  // onDone que el login por password → char select y GameRoom no cambian.
  const discordBtn = createButton('', { fullWidth: true });
  discordBtn.classList.add('aureum-btn--discord');
  discordBtn.style.marginTop = '10px';
  // Logo oficial de Discord (marca Clyde) inline como SVG blanco + el texto.
  discordBtn.innerHTML =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">' +
    '<path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>' +
    '</svg><span>Entrar con Discord</span>';
  discordBtn.onclick = async (): Promise<void> => {
    errorLabel.textContent = '';
    discordBtn.disabled = true;
    const result = await api.loginWithDiscordPopup();
    discordBtn.disabled = false;
    if (!result.success || !result.sessionToken) {
      errorLabel.textContent = result.error ?? 'Error de login con Discord';
      return;
    }
    onDone(result.sessionToken);
  };

  // Link para ir a registro (debajo del botón, con underline, sin estilo de botón)
  const registerLink = document.createElement('a');
  registerLink.textContent = '¿No tienes cuenta? Regístrate';
  registerLink.style.display = 'block';
  registerLink.style.textAlign = 'center';
  registerLink.style.marginTop = '12px';
  registerLink.style.cursor = 'pointer';
  registerLink.style.color = '#caa15a';
  registerLink.style.textDecoration = 'underline';
  registerLink.style.fontSize = '14px';
  registerLink.style.background = 'none';
  registerLink.style.border = 'none';
  registerLink.style.padding = '0';
  registerLink.onclick = (e) => {
    e.preventDefault();
    onSwitchToRegister();
  };

  container.append(
    info,
    usernameField.wrapper,
    passwordField.wrapper,
    rememberWrapper,
    errorLabel,
    submit,
    discordBtn,
    registerLink
  );
}

export function renderRegisterForm(
  api: AuthApi,
  container: HTMLDivElement,
  onDone: (sessionToken: string) => void,
  onSwitchToLogin: () => void
): void {
  container.innerHTML = '';
  container.setAttribute('data-lpignore', 'true');

  const info = document.createElement('p');
  info.textContent = 'Crear nueva cuenta';
  info.style.textAlign = 'center';
  info.style.margin = '0 0 10px';

  const usernameField = createTextInput({
    id: 'register-username',
    name: 'username',
    placeholder: 'username',
    autocomplete: 'off',
  });
  const usernameInput = usernameField.input;
  usernameField.wrapper.style.marginBottom = '8px';

  const emailField = createTextInput({
    id: 'register-email',
    name: 'email',
    placeholder: 'email',
    type: 'email',
    autocomplete: 'off',
  });
  const emailInput = emailField.input;
  emailField.wrapper.style.marginBottom = '8px';

  const passwordField = createTextInput({
    id: 'register-password',
    name: 'password',
    placeholder: 'password',
    type: 'password',
    autocomplete: 'off',
  });
  const passwordInput = passwordField.input;
  passwordField.wrapper.style.marginBottom = '8px';

  const errorLabel = document.createElement('div');
  errorLabel.style.color = '#f55';
  errorLabel.style.minHeight = '20px';
  errorLabel.style.fontSize = '12px';

  const submit = createButton('Crear cuenta y continuar', { fullWidth: true });
  submit.style.marginTop = '8px';

  submit.onclick = async () => {
    errorLabel.textContent = '';
    const username = usernameInput.value.trim();
    const email = emailInput.value.trim();
    const password = passwordInput.value;
    if (!username || !email || !password) {
      errorLabel.textContent = 'Completa todos los campos.';
      return;
    }

    submit.disabled = true;
    const regResult: RegisterResult = await api.register(username, email, password);
    submit.disabled = false;

    if (!regResult.success) {
      errorLabel.textContent = regResult.error ?? 'Error de registro';
      return;
    }

    // Auto-login después de registrar
    const loginResult = await api.login(username, password);
    if (!loginResult.success || !loginResult.sessionToken) {
      errorLabel.textContent = loginResult.error ?? 'Error de login luego de registrar';
      return;
    }

    onDone(loginResult.sessionToken);
  };

  // Link para volver a login (debajo del botón, con underline, sin estilo de botón)
  const loginLink = document.createElement('a');
  loginLink.textContent = '¿Ya tienes cuenta? Inicia sesión';
  loginLink.style.display = 'block';
  loginLink.style.textAlign = 'center';
  loginLink.style.marginTop = '12px';
  loginLink.style.cursor = 'pointer';
  loginLink.style.color = '#caa15a';
  loginLink.style.textDecoration = 'underline';
  loginLink.style.fontSize = '14px';
  loginLink.style.background = 'none';
  loginLink.style.border = 'none';
  loginLink.style.padding = '0';
  loginLink.onclick = (e) => {
    e.preventDefault();
    onSwitchToLogin();
  };

  container.append(info, usernameField.wrapper, emailField.wrapper, passwordField.wrapper, errorLabel, submit, loginLink);
}
